/**
 * Cloudflare Turnstile: bewijst dat er een mens achter de browser zit. Het token wisselen we bij de
 * API in voor een nonce (zie docs). Het script laden we pas als de modal opent.
 */
import { EmbedError } from './errors';
import { log } from './config';

interface TurnstileRenderOptions {
  sitekey: string;
  callback: (token: string) => void;
  'error-callback': (code?: string) => void;
  'expired-callback': () => void;
  'timeout-callback': () => void;
  appearance: 'always' | 'execute' | 'interaction-only';
  size?: 'normal' | 'compact' | 'flexible';
  language?: string;
}

interface TurnstileApi {
  render(container: HTMLElement, options: TurnstileRenderOptions): string;
  reset(widgetId: string): void;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let loading: Promise<TurnstileApi> | null = null;

function loadScript(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!loading) {
    loading = new Promise<TurnstileApi>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SCRIPT_URL;
      script.async = true;
      script.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new EmbedError('TURNSTILE_UNAVAILABLE')));
      script.onerror = () => {
        loading = null;
        reject(new EmbedError('TURNSTILE_UNAVAILABLE'));
      };
      document.head.appendChild(script);
    });
  }
  return loading;
}

/** Eén widget per modal. Een token is eenmalig; na gebruik `reset()` voor een nieuw token. */
export class TurnstileSession {
  private widgetId: string | null = null;
  private token: string | null = null;
  private failed = false;
  private waiters: Array<{ resolve: (t: string) => void; reject: (e: EmbedError) => void }> = [];

  constructor(
    private readonly container: HTMLElement,
    private readonly siteKey: string,
  ) {}

  async start(): Promise<void> {
    if (this.widgetId) return;
    const api = await loadScript();
    this.widgetId = api.render(this.container, {
      sitekey: this.siteKey,
      appearance: 'interaction-only',
      size: 'flexible',
      language: 'nl',
      callback: (token) => {
        log('turnstile token ontvangen');
        this.token = token;
        this.failed = false;
        this.waiters.splice(0).forEach((w) => w.resolve(token));
      },
      'error-callback': (code) => {
        log('turnstile fout', code);
        this.failed = true;
        this.waiters.splice(0).forEach((w) => w.reject(new EmbedError('TURNSTILE_FAILED')));
      },
      'expired-callback': () => {
        this.token = null;
        this.reset();
      },
      'timeout-callback': () => {
        this.token = null;
        this.reset();
      },
    });
  }

  /** Geeft een ongebruikt token; wacht zo nodig tot Turnstile er een heeft. */
  async getToken(timeoutMs = 30000): Promise<string> {
    await this.start();
    if (this.token) {
      const token = this.token;
      this.token = null;
      return token;
    }
    if (this.failed) this.reset();
    return new Promise<string>((resolve, reject) => {
      const timer = window.setTimeout(() => reject(new EmbedError('TURNSTILE_FAILED')), timeoutMs);
      this.waiters.push({
        resolve: (t) => {
          window.clearTimeout(timer);
          this.token = null;
          resolve(t);
        },
        reject: (e) => {
          window.clearTimeout(timer);
          reject(e);
        },
      });
    });
  }

  reset(): void {
    this.failed = false;
    if (this.widgetId && window.turnstile) window.turnstile.reset(this.widgetId);
  }

  destroy(): void {
    if (this.widgetId && window.turnstile) window.turnstile.remove(this.widgetId);
    this.widgetId = null;
    this.token = null;
    this.waiters.splice(0).forEach((w) => w.reject(new EmbedError('CANCELED')));
  }
}
